import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supplierModel } from '../../../models/supplierModel';
import { getSettings } from '../../../models/settingsModel';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { toast } from 'react-toastify';
import '../../../styles/colors.css';

function SupplierOrders() {
  const navigate = useNavigate();
  const { supplier } = useSupplierAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchOrders();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await supplierModel.getOrders();
      
      if (response.status === 1 || response.data) {
        const ordersData = response.data?.orders || response.orders || [];
        setOrders(ordersData);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPO = async (order) => {
    try {
      const filePath = `${order.quotation_directory}/${order.requisition_po}`;
      const fileName = order.requisition_po;
      
      await supplierModel.downloadFile(filePath, fileName);
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading PO:', error);
      toast.error('Failed to download purchase order');
    }
  };

  const handleViewInvoicing = (order) => {
    try {
      // Navigate to order details page for invoicing
      const encryptedOrderId = btoa(order.requisition_id); // Basic encoding, replace with proper encryption
      navigate(`/supplier/order/${encryptedOrderId}`);
    } catch (error) {
      console.error('Error navigating to order details:', error);
      toast.error('Failed to open order details');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading purchase orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between">
            {/* Left side - Title and info */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Purchase Orders</h1>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <span>Awarded Purchase Orders & Contracts</span>
                  <span>•</span>
                  <span>{orders.length} items</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Debug Button */}
            <div className="flex items-center gap-4">
              {/* Debug Button - Only show in development mode */}
              {settings?.debug_mode === '1' && (
                <button
                  onClick={() => setShowDebugModal(true)}
                  className="text-xs font-bold px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 transition"
                  title="Open Debug Information Modal"
                >
                  🐛 Debug
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Order
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoicing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Note
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 text-lg">No purchase orders found.</p>
                      <p className="text-gray-400 text-sm mt-1">Awarded purchase orders will appear here.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="text-gray-900 font-medium">
                        {order.specification_title || order.procurement_title}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 focus:ring-2 focus:ring-gray-500 focus:outline-none transition-colors"
                        onClick={() => handleDownloadPO(order)}
                        title="Download Purchase Order"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 hover:border-green-700 focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors"
                        onClick={() => handleViewInvoicing(order)}
                        title="View Invoicing Details"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {order.requisition_po_note && (
                        <span 
                          className="text-gray-600 text-sm"
                          title={order.requisition_po_note}
                        >
                          {order.requisition_po_note.length > 50 
                            ? `${order.requisition_po_note.substring(0, 50)}...` 
                            : order.requisition_po_note
                          }
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {orders.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <h5 className="text-lg font-semibold text-gray-900">Purchase Order Summary</h5>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-blue-600 mb-2">{orders.length}</div>
                <p className="text-sm text-gray-600 font-medium">Total Orders</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {orders.filter(order => order.requisition_status === 'completed').length}
                </div>
                <p className="text-sm text-gray-600 font-medium">Completed</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-yellow-600 mb-2">
                  {orders.filter(order => order.requisition_status === 'in_progress' || !order.requisition_status).length}
                </div>
                <p className="text-sm text-gray-600 font-medium">In Progress</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="text-3xl font-bold text-cyan-600 mb-2">
                  {orders.filter(order => order.requisition_po_note).length}
                </div>
                <p className="text-sm text-gray-600 font-medium">With Notes</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h5 className="text-lg font-semibold text-gray-900">Information</h5>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h6 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Purchase Orders
              </h6>
              <p className="text-gray-600">
                Click the "Download" button to get a copy of the official purchase order document.
              </p>
            </div>
            <div>
              <h6 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Invoicing
              </h6>
              <p className="text-gray-600">
                Click "View" to access detailed invoicing information and submit delivery confirmations.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Modal */}
      {settings?.debug_mode === '1' && (
        <DebugModal 
          show={showDebugModal}
          onHide={() => setShowDebugModal(false)}
          orders={orders}
          supplier={supplier}
          allData={{
            orders,
            supplier,
            loading,
            token: localStorage.getItem('supplier_token')
          }}
        />
      )}
    </div>
  );
}

// Debug Modal Component for Orders
const DebugModal = ({ show, onHide, orders, supplier, allData }) => {
  const [selectedSection, setSelectedSection] = useState('overview');
  
  if (!show) return null;

  const dataStructure = {
    overview: {
      title: 'Overview',
      icon: '📋',
      data: {
        supplier_name: supplier?.supplier_user_name || supplier?.Contact_Person || supplier?.BP_Name,
        supplier_email: supplier?.supplier_user_email || supplier?.E_Mail,
        supplier_id: supplier?.supplier_user_supplier_id || supplier?.VendorID || supplier?.BP_Code,
        orders_count: orders?.length || 0,
        token_exists: !!localStorage.getItem('supplier_token'),
        loading_state: allData?.loading
      }
    },
    orders: {
      title: 'Orders Data',
      icon: '📦',
      data: orders || []
    },
    supplier_context: {
      title: 'Supplier Context',
      icon: '👤',
      data: supplier || {}
    },
    authentication: {
      title: 'Authentication',
      icon: '🔐',
      data: {
        token: allData?.token ? 'Present' : 'Missing',
        token_length: allData?.token?.length || 0,
        localStorage_keys: Object.keys(localStorage),
        supplier_login_status: supplier ? 'Logged in' : 'Not logged in'
      }
    },
    raw: {
      title: 'Raw Data',
      icon: '🔧',
      data: allData
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${show ? 'block' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onHide}></div>
      <div className="relative bg-white rounded-lg shadow-xl max-w-7xl mx-auto mt-4 mb-4 h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white p-4 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold flex items-center">
            <svg className="inline-block mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Debug Information - Supplier Orders
          </h3>
          <button 
            onClick={onHide}
            className="text-white hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Navigation Sidebar */}
          <div className="w-1/4 border-r border-gray-300 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <h6 className="text-sm font-bold mb-4 uppercase tracking-wider text-gray-700">
                Data Sections
              </h6>
              <div className="space-y-1">
                {Object.entries(dataStructure).map(([key, section]) => (
                  <button
                    key={key}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                      selectedSection === key
                        ? 'bg-red-500 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedSection(key)}
                  >
                    <span className="mr-2">{section.icon}</span>
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden flex flex-col bg-white">
            <div className="p-6 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3">{dataStructure[selectedSection]?.icon}</span>
                <h4 className="text-xl font-semibold text-gray-900">
                  {dataStructure[selectedSection]?.title}
                </h4>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden px-6 pb-6">
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm h-full overflow-auto border border-gray-300">
                <pre className="whitespace-pre-wrap text-gray-100">
                  {JSON.stringify(dataStructure[selectedSection]?.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierOrders;
